<template>
  <div>
    <Head title="Sincronización" />

    <h1 class="mb-6 text-2xl font-bold text-gray-800 flex items-center gap-3">
      <span class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-teal-100">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </span>
      Sincronización Bidireccional
    </h1>

    <!-- Tarjetas de estado -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Último Push</div>
        <div class="text-sm font-medium text-gray-700">{{ lastPushedAt ?? 'Nunca' }}</div>
        <div class="text-xs text-gray-400 mt-1">local → remoto</div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Último Pull</div>
        <div class="text-sm font-medium text-gray-700">{{ lastPulledAt ?? 'Nunca' }}</div>
        <div class="text-xs text-gray-400 mt-1">remoto → local</div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Enviados</div>
        <div class="text-2xl font-bold text-blue-600">{{ pushed.toLocaleString() }}</div>
        <div class="text-xs text-gray-400 mt-1">registros↑</div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Recibidos</div>
        <div class="text-2xl font-bold text-teal-600">{{ pulled.toLocaleString() }}</div>
        <div class="text-xs text-gray-400 mt-1">registros↓</div>
      </div>
    </div>

    <!-- Estado actual + botones -->
    <div class="flex flex-wrap items-center gap-3 mb-6">

      <!-- Indicador de estado -->
      <div class="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm">
        <span v-if="status === 'idle'"       class="w-2 h-2 rounded-full bg-gray-400"></span>
        <span v-else-if="status === 'push'"  class="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
        <span v-else-if="status === 'pull'"  class="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
        <span v-else-if="status === 'done-ok'"    class="w-2 h-2 rounded-full bg-green-500"></span>
        <span v-else-if="status === 'done-error'" class="w-2 h-2 rounded-full bg-red-500"></span>
        <span :class="{
          'text-gray-500':  status === 'idle',
          'text-blue-600':  status === 'push',
          'text-teal-600':  status === 'pull',
          'text-green-600': status === 'done-ok',
          'text-red-600':   status === 'done-error',
        }">{{ statusLabel }}</span>
      </div>

      <button
        @click="startSync"
        :disabled="isRunning"
        class="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-sm"
      >
        <svg v-if="!isRunning" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <svg v-else class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {{ isRunning ? 'Sincronizando...' : 'Iniciar Sincronización' }}
      </button>

      <button
        v-if="logs.length > 0"
        @click="clearLogs"
        :disabled="isRunning"
        class="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 font-medium rounded-lg transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        Limpiar
      </button>

      <Link href="/dashboard" class="ml-auto inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors">
        ← Volver
      </Link>
    </div>

    <!-- Terminal -->
    <div class="bg-gray-900 rounded-xl shadow-inner overflow-hidden border border-gray-700">
      <!-- Barra del terminal -->
      <div class="flex items-center gap-2 px-4 py-3 bg-gray-800 border-b border-gray-700">
        <span class="w-3 h-3 rounded-full bg-red-500"></span>
        <span class="w-3 h-3 rounded-full bg-yellow-500"></span>
        <span class="w-3 h-3 rounded-full bg-green-500"></span>
        <span class="ml-3 text-xs text-gray-400 font-mono">sync — push + pull — log en tiempo real</span>
        <button @click="scrollToBottom" title="Ir al final" class="ml-auto text-gray-500 hover:text-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <!-- Área de logs -->
      <div ref="logContainer" class="p-4 font-mono text-xs leading-relaxed overflow-y-auto" style="min-height: 420px; max-height: 620px;">

        <div v-if="logs.length === 0" class="text-gray-600 italic">
          Presiona "Iniciar Sincronización" para comenzar. Se ejecutará PUSH (local→remoto) y luego PULL (remoto→local).
        </div>

        <template v-for="(log, i) in logs" :key="i">
          <!-- Separador de fase -->
          <div v-if="log.type === 'phase'" class="my-2 text-yellow-400 font-bold tracking-wide">
            {{ log.message }}
          </div>

          <!-- Log normal -->
          <div v-else class="flex gap-3 mb-0.5">
            <span class="text-gray-600 flex-shrink-0 select-none">{{ formatTime(log.timestamp) }}</span>
            <span class="flex-shrink-0 select-none" :class="iconClass(log.type)">{{ iconChar(log.type) }}</span>
            <span :class="textClass(log.type)">{{ log.message }}</span>
          </div>
        </template>

        <!-- Cursor parpadeante -->
        <div v-if="isRunning" class="flex gap-3 mt-1">
          <span class="text-gray-600 select-none">{{ currentTime }}</span>
          <span class="text-teal-400 animate-pulse">█</span>
        </div>
      </div>
    </div>

    <!-- Resultado final -->
    <div v-if="status === 'done-ok' || status === 'done-error'"
      class="mt-4 rounded-xl p-4 flex items-start gap-3"
      :class="status === 'done-ok' ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'"
    >
      <svg v-if="status === 'done-ok'" xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <div>
        <p class="font-semibold" :class="status === 'done-ok' ? 'text-green-700' : 'text-amber-700'">
          {{ status === 'done-ok' ? '¡Sincronización completada!' : 'Sincronización con errores' }}
        </p>
        <p class="text-sm mt-0.5" :class="status === 'done-ok' ? 'text-green-600' : 'text-amber-600'">
          ↑ {{ pushed.toLocaleString() }} enviados al remoto &nbsp;·&nbsp; ↓ {{ pulled.toLocaleString() }} recibidos del remoto
          <span v-if="status === 'done-error'"> — revisa el log para ver los errores</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: { Head, Link },

  props: {
    lastPulledAt: { type: String, default: null },
    lastPushedAt: { type: String, default: null },
  },

  data() {
    return {
      status: 'idle',   // idle | push | pull | done-ok | done-error
      logs: [],
      pushed: 0,
      pulled: 0,
      currentTime: '',
      clockInterval: null,
    }
  },

  computed: {
    isRunning() {
      return this.status === 'push' || this.status === 'pull'
    },
    statusLabel() {
      switch (this.status) {
        case 'idle':        return 'En espera'
        case 'push':        return 'Enviando al servidor remoto...'
        case 'pull':        return 'Recibiendo del servidor remoto...'
        case 'done-ok':     return 'Completado'
        case 'done-error':  return 'Finalizado con errores'
        default:            return ''
      }
    },
  },

  mounted() {
    this.clockInterval = setInterval(() => {
      this.currentTime = new Date().toLocaleTimeString('es-VE', { hour12: false })
    }, 500)
  },

  beforeUnmount() {
    clearInterval(this.clockInterval)
  },

  methods: {
    startSync() {
      if (this.isRunning) return

      if (!navigator.onLine) {
        this.$toast?.warning('Sin conexión a internet.')
        return
      }

      this.status = 'push'
      this.pushed = 0
      this.pulled = 0
      this.logs   = []

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''

      fetch('/sync/stream', {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Accept':        'text/event-stream',
          'X-CSRF-TOKEN':  csrfToken,
        },
      }).then(response => {
        if (!response.ok) {
          this.addLog('error', `Error HTTP ${response.status}`)
          this.status = 'done-error'
          return
        }

        const reader  = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer    = ''

        const read = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              if (this.isRunning) {
                this.status = 'done-error'
                this.addLog('error', 'Conexión cerrada inesperadamente')
              }
              return
            }

            buffer += decoder.decode(value, { stream: true })

            const events = buffer.split('\n\n')
            buffer = events.pop() ?? ''

            for (const block of events) {
              if (!block.trim()) continue

              let eventType = 'info'
              let data      = null

              for (const line of block.split('\n')) {
                if (line.startsWith('event: '))      eventType = line.substring(7).trim()
                else if (line.startsWith('data: '))  {
                  try { data = JSON.parse(line.substring(6)) } catch {}
                }
              }

              if (data) this.handleEvent(eventType, data)
            }

            read()
          }).catch(err => {
            if (this.isRunning) {
              this.addLog('error', 'Error de conexión: ' + err.message)
              this.status = 'done-error'
            }
          })
        }

        read()

      }).catch(err => {
        this.addLog('error', 'No se pudo conectar: ' + err.message)
        this.status = 'done-error'
      })
    },

    handleEvent(type, data) {
      if (type === 'done') {
        this.status = data.success ? 'done-ok' : 'done-error'
        if (data.pushed !== undefined) this.pushed = data.pushed
        if (data.pulled !== undefined) this.pulled = data.pulled
        return
      }

      // Actualizar fase en el status
      if (type === 'phase') {
        if (data.message.includes('PULL')) this.status = 'pull'
        else if (data.message.includes('PUSH')) this.status = 'push'
      }

      this.addLog(type, data.message, data.timestamp)
    },

    addLog(type, message, timestamp = null) {
      this.logs.push({ type, message, timestamp: timestamp ?? new Date().toISOString() })
      this.$nextTick(() => this.scrollToBottom())
    },

    scrollToBottom() {
      const el = this.$refs.logContainer
      if (el) el.scrollTop = el.scrollHeight
    },

    clearLogs() {
      this.logs   = []
      this.status = 'idle'
      this.pushed = 0
      this.pulled = 0
    },

    formatTime(iso) {
      try { return new Date(iso).toLocaleTimeString('es-VE', { hour12: false }) }
      catch { return iso }
    },

    iconClass(type) {
      return {
        success: 'text-green-400',
        error:   'text-red-400',
        warning: 'text-yellow-400',
        start:   'text-teal-400',
      }[type] ?? 'text-blue-400'
    },

    textClass(type) {
      return {
        success: 'text-green-300',
        error:   'text-red-300',
        warning: 'text-yellow-300',
        start:   'text-teal-300',
      }[type] ?? 'text-gray-300'
    },

    iconChar(type) {
      return { success: '✓', error: '✗', warning: '⚠', start: '▶' }[type] ?? '·'
    },
  },
}
</script>
